import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class SystemLogsService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );
            return {
                success: true,
                data: response.data,
                pagination: response.pagination || {},
                uniqueValues: response.uniqueValues || {},
                message: response.message || 'Sistem logları getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch system logs');
        }
    }

    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch log entry');
        }
    }

    async getStatSummary(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getStatSummary(params),
                { maxRetries: 2 }
            );
            return { success: true, data: response.data };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch stat summary');
        }
    }

    async getStats(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getStats(params),
                { maxRetries: 2 }
            );
            return { success: true, data: response.data };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch statistics');
        }
    }

    async getHeatmap(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getHeatmap(params),
                { maxRetries: 2 }
            );
            return { success: true, data: response.data };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch heatmap data');
        }
    }

    async getSecuritySummary() {
        try {
            const response = await withRetry(
                () => this.api.getSecuritySummary(),
                { maxRetries: 2 }
            );
            return { success: true, data: response.data };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch security summary');
        }
    }

    async getRetentionInfo() {
        try {
            const response = await withRetry(
                () => this.api.getRetentionInfo(),
                { maxRetries: 2 }
            );
            return { success: true, data: response.data };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch retention info');
        }
    }

    async getUserActivity(userId, params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getUserActivity(userId, params),
                { maxRetries: 2 }
            );
            return {
                success: true,
                data: response.data,
                pagination: response.pagination || {}
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch user activity');
        }
    }

    async getDeltaPicker(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getDeltaPicker(params),
                { maxRetries: 2 }
            );
            return { success: true, data: response.data };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch delta picker');
        }
    }

    async getTimeline(resourceType, resourceId, params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getTimeline(resourceType, resourceId, params),
                { maxRetries: 2 }
            );
            return {
                success: true,
                data: response.data,
                pagination: response.pagination || {}
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch timeline');
        }
    }

    async estimateExport(filters = {}) {
        try {
            const response = await this.api.estimateExport(filters);
            return { success: true, data: response.data };
        } catch (error) {
            throw this._handleError(error, 'Failed to estimate export');
        }
    }

    async exportLogs(filters = {}, format = 'json') {
        try {
            const response = await this.api.exportLogs({ filters, format });
            return {
                success: true,
                data: response.data || response,
                total: response.total,
                metadata: response.metadata,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to export logs');
        }
    }

    // Plan Faz 9.7 — append-only hash-chain integrity + entity timeline
    async verifyIntegrity(params = {}) {
        try {
            const response = await this.api.verifyIntegrity(params);
            return { success: true, data: response.data };
        } catch (error) {
            throw this._handleError(error, 'Failed to verify integrity');
        }
    }

    async getEntityActivity(entityType, entityId, params = {}) {
        try {
            const response = await this.api.getEntityActivity(entityType, entityId, params);
            return {
                success: true,
                data: response.data,
                pagination: response.pagination || {},
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch entity activity');
        }
    }

    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) throw error;
        if (error.statusCode || error.status) throw ApiError.fromResponse(error);
        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
